/**
 * Project detail slice (PX-W6.3, TASK-EDD04F2F) — the engagement workspace's
 * backing state: the loaded project row, its roster, and the BLOCKED state a
 * non-member sees.
 *
 * `openProject` loads the project row and the roster in ONE thunk so the
 * workspace mounts atomically. A keyed 403/404 does NOT land as an error —
 * it sets `blocked` (status + code + message rendered verbatim, AC-7), which
 * the page renders instead of an empty workspace.
 *
 * Roster mutations are never optimistic: each refetches the roster from the
 * server on fulfilment (the server re-derives sides and re-stamps rows; a
 * client-side roster write would be a second, drifting source of truth).
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  markServerChanged,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { projectService } from '../services/projectService.http'
import type {
  AddMemberInput,
  ProjectMemberRow,
  ProjectSummary,
  UpdateMemberInput
} from '../types'
import { projectsBusFrame } from './projectsSlice'

/**
 * The server refusal a non-member sees — rendered verbatim, never fabricated.
 *
 * ⚠ `kind` is the DISCRIMINANT of the rejection union, and it is load-bearing.
 * The reducer used to tell a refusal from an ordinary failure by testing
 * `'status' in payload` — which stopped working the moment the failure type
 * gained its own status (the C2 fix-back), silently routing every 500 into the
 * blocked card. A structural test over optional fields is a discriminator that
 * can be broken from a distance; a literal tag cannot.
 */
export interface ProjectBlocked {
  kind: 'blocked'
  status: number
  code?: string
  message: string
}

export interface ProjectDetailError {
  /**
   * The SERVER's HTTP status, carried verbatim. Without it a 403 is
   * structurally indistinguishable from a 500 by the time it reaches a page,
   * which is how AC-6.2 shipped unimplemented on two surfaces (merged review
   * C2): they hardcoded a status into `ErrorPanel` and could never branch to
   * `ForbiddenPanel`. `0` means the request never reached the server.
   */
  status: number
  key?: string
  message: string
}

export interface ProjectDetailState extends FetchCache {
  /** Slug of the project on screen; null when no workspace is mounted. */
  projectId: string | null
  project: ProjectSummary | null
  members: ProjectMemberRow[]
  /** A keyed 403/404 — the page renders the blocked state, not an error card. */
  blocked: ProjectBlocked | null
  error: ProjectDetailError | null
  /** Roster fetch in flight (openProject + the post-mutation refetch). */
  membersLoading: boolean
  membersError: ProjectDetailError | null
}

const initialState: ProjectDetailState = {
  projectId: null,
  project: null,
  members: [],
  blocked: null,
  error: null,
  membersLoading: false,
  membersError: null,
  ...initialFetchCache
}

/** Map any thrown value to a detail-shaped error payload. */
const toDetailError = (error: unknown): ProjectDetailError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, key: apiError.backendKey, message: apiError.message }
}

/** The keyed refusal a non-member receives — or null for an ordinary failure. */
const asBlocked = (error: unknown): ProjectBlocked | null => {
  if (!isApiError(error)) return null
  if (error.status !== 403 && error.status !== 404) return null
  return { kind: 'blocked', status: error.status, code: error.backendKey, message: error.message }
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

interface ProjectArg {
  orgId: string
  projectId: string
}

interface OpenProjectResult {
  project: ProjectSummary
  members: ProjectMemberRow[]
}

/**
 * Mount the workspace for one project: the caller-permissioned row plus the
 * roster, atomically. A 403/404 sets `blocked` and clears the row (AC-7).
 */
export const openProject = createAsyncThunk<
  OpenProjectResult,
  ProjectArg,
  { rejectValue: ProjectBlocked | ProjectDetailError }
>('projectDetail/open', async ({ orgId, projectId }, { rejectWithValue }) => {
  try {
    const [project, members] = await Promise.all([
      projectService.get(orgId, projectId),
      projectService.listMembers(orgId, projectId)
    ])
    return { project, members }
  } catch (error) {
    const blocked = asBlocked(error)
    if (blocked !== null) return rejectWithValue(blocked)
    return rejectWithValue(toDetailError(error))
  }
}, {
  condition: ({ projectId }, { getState }) => {
    const { projectDetail } = getState() as { projectDetail: ProjectDetailState }
    return shouldFetch(projectDetail, projectId)
  },
  // A vetoed open is still an OPEN: it dispatches `rejected` with
  // `meta.condition`, and the reducer re-asserts the project on screen
  // (TASK-3D6DB8B6 fix cycle 2 — see the rejected case). No request is sent.
  dispatchConditionRejection: true
})

// (closeProject is a PLAIN reducer action — see the reducers block. Review
// M-d: it performs no I/O, so pretending to be a thunk was dishonest shape.)

/** Refetch ONLY the roster (after a member mutation). */
export const refreshMembers = createAsyncThunk<
  ProjectMemberRow[],
  ProjectArg,
  { rejectValue: ProjectDetailError }
>('projectDetail/refreshMembers', async ({ orgId, projectId }, { rejectWithValue }) => {
  try {
    return await projectService.listMembers(orgId, projectId)
  } catch (error) {
    return rejectWithValue(toDetailError(error))
  }
})

/** Add a member, then refetch the roster (no optimistic roster writes). */
export const addMember = createAsyncThunk<
  void,
  ProjectArg & { input: AddMemberInput },
  { rejectValue: ProjectDetailError }
>('projectDetail/addMember', async ({ orgId, projectId, input }, { dispatch, rejectWithValue }) => {
  try {
    await projectService.addMember(orgId, projectId, input)
    await dispatch(refreshMembers({ orgId, projectId }))
  } catch (error) {
    return rejectWithValue(toDetailError(error))
  }
})

/** Patch a member, then refetch the roster. */
export const updateMember = createAsyncThunk<
  void,
  ProjectArg & { userId: string; patch: UpdateMemberInput },
  { rejectValue: ProjectDetailError }
>(
  'projectDetail/updateMember',
  async ({ orgId, projectId, userId, patch }, { dispatch, rejectWithValue }) => {
    try {
      await projectService.updateMember(orgId, projectId, userId, patch)
      await dispatch(refreshMembers({ orgId, projectId }))
    } catch (error) {
      return rejectWithValue(toDetailError(error))
    }
  }
)

/** Remove a member, then refetch the roster. */
export const removeMember = createAsyncThunk<
  void,
  ProjectArg & { userId: string },
  { rejectValue: ProjectDetailError }
>('projectDetail/removeMember', async ({ orgId, projectId, userId }, { dispatch, rejectWithValue }) => {
  try {
    await projectService.removeMember(orgId, projectId, userId)
    await dispatch(refreshMembers({ orgId, projectId }))
  } catch (error) {
    return rejectWithValue(toDetailError(error))
  }
})

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const projectDetailSlice = createSlice({
  name: 'projectDetail',
  initialState,
  reducers: {
    /**
     * Leave the workspace — resets every field. A PLAIN action (review M-d):
     * leaving a page performs no I/O, so there is nothing to await and no
     * fulfilled case to handle.
     */
    closeProject(state) {
      state.projectId = null
      state.project = null
      state.members = []
      state.blocked = null
      state.error = null
      state.membersLoading = false
      state.membersError = null
      invalidate(state)
    },
    /**
     * Direct-action seam for a full server row of THIS project — spread-
     * merged; a row for any other project never touches the open workspace.
     * The BUS path no longer merges: its frames are id-only and route through
     * the `projectsBusFrame` case below (stale-mark + REST refetch).
     */
    detailProjectUpdated(state, action: PayloadAction<ProjectSummary>) {
      if (state.projectId === action.payload.id && state.project !== null) {
        state.project = { ...state.project, ...action.payload }
      }
    },
    /** Dismiss a workspace load error and re-arm the cache (the Retry path). */
    clearProjectDetailError(state) {
      state.error = null
      invalidate(state)
    },
    /** Dismiss a roster mutation refusal (rendered inline by the panel). */
    clearMemberError(state) {
      state.membersError = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(openProject.pending, (state, action) => {
        // A different project mounting resets the whole workspace first, so a
        // slow unmount of project A can never paint onto project B.
        if (state.projectId !== action.meta.arg.projectId) {
          state.project = null
          state.members = []
          state.blocked = null
          state.error = null
          state.membersError = null
        }
        fetchStarted(state, action.meta.arg.projectId)
        state.projectId = action.meta.arg.projectId
        state.error = null
        state.membersLoading = true
      })
      .addCase(openProject.fulfilled, (state, action) => {
        // Key-scoped (TASK-3D6DB8B6): a superseded project's reply writes nothing.
        if (!fetchSucceeded(state, action.meta.arg.projectId)) return
        state.project = action.payload.project
        state.members = action.payload.members
        state.blocked = null
        state.membersLoading = false
      })
      .addCase(openProject.rejected, (state, action) => {
        const key = action.meta.arg.projectId
        if (action.meta.condition) {
          // A VETOED open (TASK-3D6DB8B6 fix cycle 2): this project's load is
          // already in flight, or its row is already fresh — either way the
          // store holds (or is about to hold) THIS project, so the workspace is
          // re-asserted on screen. Without it, StrictMode's close-between-
          // effects (and any close-then-reopen mid-load) left `projectId` null
          // with the row landed, and every `projectId === id` guard — the frame
          // refetch listener, the frame case below, the follow-up's on-screen
          // check — went dead. The cache and its in-flight marker are untouched
          // (C-3), so no second load is started.
          state.projectId = key
          if (state.loading && state.loadingKey === key) state.membersLoading = true
          return
        }
        if (!fetchFailed(state, key)) return
        state.membersLoading = false
        const payload = action.payload
        if (payload !== undefined && 'kind' in payload && payload.kind === 'blocked') {
          // The keyed refusal: blocked state, no empty workspace (AC-7).
          state.blocked = payload
          state.project = null
          state.members = []
        } else {
          state.error = payload ?? null
        }
      })
      .addCase(refreshMembers.pending, (state) => {
        state.membersLoading = true
        state.membersError = null
      })
      .addCase(refreshMembers.fulfilled, (state, action) => {
        state.members = action.payload
        state.membersLoading = false
      })
      .addCase(refreshMembers.rejected, (state, action) => {
        state.membersLoading = false
        state.membersError = action.payload ?? null
      })
      .addCase(addMember.rejected, (state, action) => {
        state.membersError = action.payload ?? null
      })
      .addCase(updateMember.rejected, (state, action) => {
        state.membersError = action.payload ?? null
      })
      .addCase(removeMember.rejected, (state, action) => {
        state.membersError = action.payload ?? null
      })
      .addCase(projectsBusFrame, (state, action) => {
        const frame = action.payload
        // id-only frame (TASK-7922504E fix cycle 1): the open project changed
        // on the server — mark it stale; `projectFrameRefetch.ts` reloads the
        // row through REST (which redacts per caller side). Never merged.
        if (
          frame.event === 'project.updated' &&
          frame.project !== undefined &&
          frame.project.id === state.projectId
        ) {
          markServerChanged(state)
        }
        // archived/deleted frames for the open project are deliberately not
        // converted into a blocked state here: the user's own archive is
        // echo-suppressed upstream, and another admin removing an open
        // engagement is surfaced by the next mount, not invented client-side.
      })
  }
})

export const {
  closeProject,
  detailProjectUpdated,
  clearProjectDetailError,
  clearMemberError
} = projectDetailSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithProjectDetail {
  projectDetail: ProjectDetailState
}

export const selectProjectDetail = (s: WithProjectDetail) => s.projectDetail.project
export const selectProjectMembers = (s: WithProjectDetail) => s.projectDetail.members
export const selectProjectDetailLoading = (s: WithProjectDetail) => s.projectDetail.loading
export const selectProjectBlocked = (s: WithProjectDetail) => s.projectDetail.blocked
export const selectProjectDetailError = (s: WithProjectDetail) => s.projectDetail.error
export const selectMembersLoading = (s: WithProjectDetail) => s.projectDetail.membersLoading
export const selectMembersError = (s: WithProjectDetail) => s.projectDetail.membersError

/** Whether the caller may WRITE this project (server projection, INV-9). */
export const selectCanWriteProject = (s: WithProjectDetail): boolean =>
  s.projectDetail.project?.permissions?.write === true
